/**
 * The panel's route literals → the Product API's bearer-authenticated
 * `OraChatProxyController` routes (`/api/workflows/orachat/*`), for a host with
 * no ora cookie session: the old Outlook add-in's transport and the pane's dev
 * proof boot (~/features/workflows/pane/assistant/paneProof).
 *
 * CLOSED: a path not in the table never reaches the network. Two are answered
 * in the browser (plan push-channel → 503, which `usePlanSignalR` treats as
 * "poll instead"; speech ack → 204).
 */

type RouteMap = ReadonlyArray<readonly [RegExp, (m: RegExpMatchArray) => string]>;

const API = "/api/workflows/orachat";

/** Panel path → Product API route (OraChatProxyController.cs). */
export const ORACHAT_API_ROUTES: RouteMap = [
  [/^\/resources\/orachat\/ask\/api$/, () => `${API}/ask`],
  [/^\/resources\/orachat\/continue\/api$/, () => `${API}/continue`],
  [/^\/resources\/orachat\/abort\/api$/, () => `${API}/abort`],
  [/^\/resources\/chat\/prefs\/api$/, () => `${API}/prefs`],
  [/^\/resources\/chat\/sessions\/api$/, () => `${API}/sessions`],
  [
    /^\/resources\/chat\/sessions\/by-conversation\/([^/]+)\/messages\/api$/,
    (m) => `${API}/sessions/by-conversation/${m[1]}/messages`,
  ],
  [/^\/resources\/chat\/sessions\/([^/]+)\/title\/api$/, (m) => `${API}/sessions/${m[1]}/title`],
  [/^\/resources\/chat\/sessions\/([^/]+)\/pin\/api$/, (m) => `${API}/sessions/${m[1]}/pin`],
  [/^\/resources\/chat\/sessions\/([^/]+)\/api$/, (m) => `${API}/sessions/${m[1]}`],
  [/^\/resources\/orachat\/plan\/([^/]+)\/api$/, (m) => `${API}/plan/${m[1]}`],
  [/^\/resources\/orachat\/voice\/stt\/api$/, () => `${API}/voice/stt`],
  // Mint, heartbeat and release all POST here (the body's `action` differs).
  [/^\/resources\/orachat\/voice\/realtime\/session\/api$/, () => `${API}/voice/realtime/session`],
];

/** Panel paths that carry the voice host and the window's id. Chat routes do not. */
export const ORACHAT_VOICE_ROUTES: ReadonlySet<string> = new Set([
  "/resources/orachat/voice/stt/api",
  "/resources/orachat/voice/realtime/session/api",
]);

function jsonResponse(status: number, body: Record<string, unknown>): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: { "Content-Type": "application/json" },
  });
}

/** Panel paths answered here, never sent. */
const LOCAL_ANSWERS: ReadonlyArray<readonly [RegExp, () => Response]> = [
  // The .NET shape is POST .../negotiate; 503 is the plan hook's "poll instead".
  [
    /^\/api\/orachat\/plan\/[^/]+\/push-channel$/,
    () => jsonResponse(503, { error: "push_channel_unavailable" }),
  ],
  // Fire-and-forget acknowledgement of a spoken reply.
  [/^\/resources\/orachat\/voice\/speech_ack\/api$/, () => new Response(null, { status: 204 })],
];

/** The Product API path for a panel path, or null when the table has none. */
export function mapOraChatPath(pathname: string): string | null {
  for (const [re, to] of ORACHAT_API_ROUTES) {
    const m = pathname.match(re);
    if (m) return to(m);
  }
  return null;
}

/** The browser's own answer for a panel path that is never sent, else null. */
export function localOraChatAnswer(pathname: string): Response | null {
  const local = LOCAL_ANSWERS.find(([re]) => re.test(pathname));
  return local ? local[1]() : null;
}

/** What a closed transport answers for a panel path the table does not map. */
export function unmappedOraChatRoute(pathname: string, correlationId?: string): Response {
  return jsonResponse(404, {
    error: "transport_unmapped_route",
    path: pathname,
    ...(correlationId ? { correlation_id: correlationId } : {}),
  });
}
